const getNumberFormatLocale = (): string => (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

export class PriceVO {
  constructor(public readonly priceAmount: number) {}

  public format(currency: string, minimumFractionDigits = 2) {
    return new Intl.NumberFormat(getNumberFormatLocale(), {
      style: 'currency',
      currency,
      minimumFractionDigits,
    }).format(this.priceAmount);
  }

  /** Omits fractional digits for whole amounts (e.g. £25) while preserving decimals when needed (e.g. £25.50). */
  public formatCompact(currency: string) {
    const isWholeAmount = this.priceAmount % 1 === 0;

    return new Intl.NumberFormat(getNumberFormatLocale(), {
      style: 'currency',
      currency,
      minimumFractionDigits: isWholeAmount ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(this.priceAmount);
  }

  public add(price: PriceVO) {
    return new PriceVO(this.priceAmount + price.priceAmount);
  }

  public substract(price: PriceVO) {
    return new PriceVO(this.priceAmount - price.priceAmount);
  }

  public substractPercentage(percent: number) {
    if (percent < 0 || percent > 100) {
      throw new Error('Percentage must be between 0 and 100');
    }

    const amountToSubtract = (this.priceAmount * percent) / 100;

    return new PriceVO(this.priceAmount - amountToSubtract);
  }

  public multiply(by: number) {
    return new PriceVO(this.priceAmount * by);
  }

  // Split price into integer and decimal parts
  public split = (): { value: string; valueDecimal: string } => {
    // Use toFixed(2) to ensure exactly 2 decimal places and handle floating point precision
    const priceString = this.priceAmount.toFixed(2);
    const [value, valueDecimal] = priceString.split('.');

    return { value, valueDecimal };
  };

  public getPriceProps(discountPercentage: number) {
    const basePrice = this.priceAmount;
    const discountedPriceResult = discountPercentage ? this.substractPercentage(discountPercentage) : null;
    const { value = basePrice, valueDecimal } = discountedPriceResult?.split() ?? {};

    return {
      oldValue: discountPercentage ? String(basePrice) : undefined,
      value: String(value),
      valueDecimal: discountedPriceResult ? valueDecimal : undefined,
    };
  }
}
